// @ts-check

/** @param {{ new(): HTMLElement }} [Base] */
export default (Base = HTMLElement) =>
  class OptionGroupMixin extends Base {
    static get observedAttributes() {
      return ["name", "value"];
    }

    /** @param {Element[]} elements */
    static getValues(elements) {
      return Array.from(elements).map((element) => element.getAttribute("value"));
    }

    static get dispatchEventName() {
      return "option-value-change";
    }

    /** @returns {Iterable<HTMLElement>} */
    get optionElements() {
      throw new Error("OptionGroupMixin: optionElements getter must be overwritten in subclass");
    }

    get name() {
      return this.getAttribute("name");
    }

    set name(value) {
      setAttr(this, "name", value);
    }

    get value() {
      return this.getAttribute("value");
    }

    set value(value) {
      setAttr(this, "value", value)
    }

    get values() {
      return Array.from(this.optionElements).map((element) => element.getAttribute("value"));
    }

    /** @returns {(string | null)[] | Set<string | null>} */
    get disabledValues() {
      return this.#instanceConstructor.getValues(
        Array.from(this.optionElements).filter((element) => element.hasAttribute("disabled"))
      );
    }

    set disabledValues(disabledValues) {
      disabledValues = disabledValues instanceof Set ? disabledValues : new Set(disabledValues);
      for (const el of this.optionElements) {
        el.toggleAttribute("disabled", disabledValues.has(el.getAttribute("value")));
      }
    }

    /** @returns {(string | null)[] | Set<string | null>} */
    get lightningDealValues() {
      return this.#instanceConstructor.getValues(
        Array.from(this.optionElements).filter((element) => element.classList.contains("lightning-deal"))
      );
    }

    set lightningDealValues(lightningDealValues) {
      lightningDealValues = lightningDealValues instanceof Set ? lightningDealValues : new Set(lightningDealValues);
      for (const el of this.optionElements) {
        el.classList.toggle("lightning-deal", lightningDealValues.has(el.getAttribute("value")));
      }
    }

    #instanceConstructor;

    constructor() {
      super();
      this.#instanceConstructor = new.target;
    }

    /** @param {Node} node @param {string} [event] */
    listenToChangeEvent(node, event = "change") {
      node.addEventListener(event, (e) => this.handleOptionChange(e));
    }

    /** @param {Event} e */
    handleOptionChange(e) {
      const { target } = e;
      if (target && 'value' in target && (target.value === null || typeof target.value === "string")) {
        this.value = target.value;
        this.dispatchEvent(
          new CustomEvent(this.#instanceConstructor.dispatchEventName, {
            bubbles: true,
            composed: true,
            detail: {
              name: this.name,
              value: this.value,
            },
          })
        );
      }
    }
  };

/** @param {HTMLElement} el @param {string} name @param {string | null} value */
function setAttr(el, name, value) {
  if (value === null) {
    el.removeAttribute("value");
  } else {
    el.setAttribute("value", value);
  }
}